import { ChangeEvent, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Paper, Stack } from '@mui/material';
import { Site } from '@/core/entities';
import Table from '@/components/ui/table';
import Pagination from '@/components/ui/pagination';
import SearchInputTable from '@/components/ui/search-input-table';
import { useSearchQuery } from '@/shared/hooks/useSearchQuery';
import { useSites } from '@/queries/sites';
import { PaginationMeta } from '@/shared/types/pagination';
import { FIRST_PAGE, PAGE_SIZE, PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import useAuth from '@/shared/context/auth-context';
import AddressCityCell from '@/features/sites/table/cells/AddressCityCell';
import MainPointOfContactCell from '@/features/sites/table/cells/MainPointOfContactCell';

// TODO: Make this reusable component for customer site management and here
const SitesPage: FunctionComponent = (): ReactElement => {
	const navigate = useNavigate();

	const { user: authUser } = useAuth();

	const [sites, setSites] = useState<Site[]>([]);
	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);
	const { currentPage, totalItems, totalPages } = paginationMeta;

	const [searchTerm, setSearchTerm] = useState('');
	const searchQuery = useSearchQuery(searchTerm);

	const customerId = useMemo(() => authUser?.customerId, [authUser]);

	const {
		query: { isLoading, data: sitePages, fetchNextPage },
	} = useSites({
		customerId,
		queryParams: { limit: PAGE_SIZE, search: searchQuery },
	});

	useEffect(
		() => setPaginationMeta((paginationMeta) => ({ ...paginationMeta, currentPage: FIRST_PAGE })),
		[searchQuery]
	);

	useEffect(() => {
		const sitePage = sitePages?.pages[currentPage - 1];
		if (sitePage) {
			const { page: currentPage, totalItems, totalPages } = sitePage;
			setSites(sitePage.data);
			setPaginationMeta((paginationMeta) => ({
				...paginationMeta,
				totalItems,
				totalPages,
				currentPage,
			}));
		}
	}, [sitePages, currentPage]);

	useEffect(() => {
		const pagesCount = sitePages?.pages.length;
		if (!pagesCount || !totalPages) return;
		if (totalPages > pagesCount)
			setPaginationMeta((paginationMeta) => ({ ...paginationMeta, currentPage: pagesCount }));
	}, [totalPages, sitePages]);

	return (
		<AdminPanelPage title="Site management">
			<Box component={Paper} p={3}>
				<Stack direction="column" spacing={2.5}>
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<SearchInputTable
							label="Search sites"
							value={searchTerm}
							onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
						/>
					</Stack>
					{isLoading ? (
						<Stack direction="column" alignItems="center">
							<CircularProgress size={20} />
						</Stack>
					) : (
						<>
							<Table<Site>
								ariaLabel="Customer sites table"
								empty="No sites available"
								data={sites}
								columns={[
									{
										name: 'Site',
										accessors: ['name'],
									},
									{
										name: 'Address, city',
										accessors: ['address', 'city'],
										render: ({ address, city }) => (
											<AddressCityCell address={address || ''} city={city || ''} />
										),
									},
									{
										name: 'Main point of contact',
										accessors: ['siteAdminName', 'siteAdminTitle', 'siteAdminEmail'],
										render: ({ siteAdminName, siteAdminTitle, siteAdminEmail }) => (
											<MainPointOfContactCell
												siteAdminName={siteAdminName || ''}
												siteAdminTitle={siteAdminTitle || ''}
												siteAdminEmail={siteAdminEmail || ''}
											/>
										),
									},
									{
										name: 'Time zone',
										accessors: ['timeZone'],
									},
									{
										name: 'No. of devices',
										accessors: ['numberOfDevices'],
										align: 'right',
									},
								]}
								rowAction={(item: Site) => navigate(`/sites/${item.id}`)}
							/>
							<Pagination
								currentPage={currentPage}
								perPage={PAGE_SIZE}
								totalItems={totalItems}
								onPageChange={(page) => {
									if (sitePages!.pages.length <= page + 1) {
										/* eslint-disable @typescript-eslint/no-floating-promises */
										fetchNextPage();
									}
									setPaginationMeta({ ...paginationMeta, currentPage: page });
								}}
							/>
						</>
					)}
				</Stack>
			</Box>
		</AdminPanelPage>
	);
};

export default SitesPage;
